import { fromJS, List, Record } from 'immutable';
import _ from 'lodash';

import { AmountCurrencies, CurrencyCode } from 'utils/currencyFormat';
import { DonationCreditRuleDto } from 'entities/donationCreditRule/dto';

const properties = {
  id: '',
  title: '',
  budget: undefined,
  categories: [],
  categoriesExclude: true,
  creditAmountPerHour: undefined,
  limitTotal: undefined,
  limitPerUser: undefined,
  currencyCode: undefined,
  totalIssued: undefined,
  totalIssuedCurrencies: undefined,
  totalRemaining: undefined,
  totalRemainingCurrencies: undefined,
  totalUsed: undefined,
  totalUsedCurrencies: undefined,
  totalExpired: undefined,
  totalExpiredCurrencies: undefined,
  creditingTransactionsCount: 0,
  startingAt: undefined,
  endingAt: undefined,
  limitHoursPerUser: undefined,
  nonprofits: [],
  nonprofitsExclude: true,
  deeds: [],
  deedsExclude: true,
  locations: [],
  locationsExclude: true,
  validityDuration: undefined,
  bypassBudgetLimitPerUser: false,
  appliesToGuests: false,
  type: undefined,
};

export default class DonationCreditRule extends Record(properties, 'DonationCreditRule') {
  public readonly id!: string;

  public readonly title!: string;

  public readonly budget!: string;

  public readonly categories!: List<string>;

  public readonly categoriesExclude!: boolean;

  public readonly creditAmountPerHour!: number;

  public readonly limitTotal!: number;

  public readonly limitPerUser!: number;

  public readonly currencyCode!: CurrencyCode;

  public readonly totalIssued!: number;

  public readonly totalIssuedCurrencies!: AmountCurrencies;

  public readonly totalRemaining!: number;

  public readonly totalRemainingCurrencies!: AmountCurrencies;

  public readonly totalUsed!: number;

  public readonly totalUsedCurrencies!: AmountCurrencies;

  public readonly totalExpired!: number;

  public readonly totalExpiredCurrencies!: AmountCurrencies;

  public readonly creditingTransactionsCount!: number;

  public readonly startingAt!: Date;

  public readonly endingAt!: Date;

  public readonly limitHoursPerUser!: number;

  public readonly nonprofits!: List<string>;

  public readonly nonprofitsExclude!: boolean;

  public readonly deeds!: List<string>;

  public readonly deedsExclude!: boolean;

  public readonly locations!: List<string>;

  public readonly locationsExclude!: boolean;

  public readonly bypassBudgetLimitPerUser!: boolean;

  public readonly appliesToGuests!: boolean;

  public readonly validityDuration!: string;

  public readonly type!: string;

  constructor(values: DonationCreditRuleDto) {
    const validValues = _.pick(values, Object.keys(properties));
    super({
      ...validValues,
      type: values.__t,
      categories: fromJS(values.categories || []),
      nonprofits: fromJS(values.nonprofits || []),
      deeds: fromJS(values.deeds || []),
      locations: fromJS(values.locations || []),
      startingAt: values.startingAt ? new Date(values.startingAt) : undefined,
      endingAt: values.endingAt ? new Date(values.endingAt) : undefined,
      budget: values.budget.id || values.budget,
    });
  }

  get isEditable() {
    return (
      !this.type ||
      ['VolunteerTimeDonationCreditRule', 'OneTimeDonationCreditRule'].includes(this.type)
    );
  }

  get validityDurationPeriods() {
    if (this.validityDuration) {
      const parsedDuration = this.validityDuration.match(/\d+/g);

      return parsedDuration ? Number(parsedDuration[0]) : null;
    }

    return null;
  }

  get validityDurationPeriod() {
    if (this.validityDuration) {
      const parsedDuration = this.validityDuration.split(/\d+/);
      return parsedDuration[parsedDuration.length - 1];
    }

    return null;
  }

  toForm() {
    const form = this.toJS();

    if (this.validityDurationPeriod && this.validityDurationPeriods) {
      form.validityDuration = {
        periods: this.validityDurationPeriods,
        period: this.validityDurationPeriod,
      };
    }

    return form;
  }

  static fromFormToRequestJson({ validityDuration, ...form }) {
    return {
      ...form,
      validityDuration:
        validityDuration?.periods && validityDuration?.period && validityDuration.periods > 0
          ? `P${validityDuration.periods}${validityDuration.period}`
          : null,
    };
  }

  get isActive(): boolean {
    const now = Date.now();

    return (
      (!this.startingAt || +this.startingAt <= now) && (!this.endingAt || +this.endingAt >= now)
    );
  }
}
